import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Moon, Sun, Search, LogOut, ChevronDown, Building2, Bell, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { visibleModules } from "@/lib/nav";
import { Avatar, Clock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { fmtBS } from "@/lib/nepali";
import { ChatDock, useChatUnread } from "@/pages/Chat";

export default function AppShell() {
  const { user, logout, switchOrg } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const chatUnread = useChatUnread();
  const [dark, setDark] = useState(() => localStorage.getItem("avocado_theme") === "dark");
  const [cmd, setCmd] = useState(false);
  const modules = visibleModules(user.permissions);

  useEffect(() => { document.documentElement.classList.toggle("dark", dark); localStorage.setItem("avocado_theme", dark ? "dark" : "light"); }, [dark]);
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmd((v) => !v); } };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, []);

  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const activeOrg = user.organizations?.find((o) => o.id === user.active_org_id) || user.org;

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-[var(--paper)] sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-5 pt-6 pb-4">
          <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo.png" alt="Avocado HRMS" className="w-9 h-9 object-contain" data-testid="brand-logo" />
            <span className="font-serif text-xl font-semibold tracking-tight">Avocado <span className="text-primary">HRMS</span></span>
          </NavLink>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">People Ledger · {activeOrg?.name}</div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-5">
          {modules.map((m, i) => (
            <div key={m.id} data-testid={`sidebar-nav-${m.id}`}>
              <div className="flex items-center gap-2 px-2.5 mb-1.5">
                <span className="font-mono text-[10px] text-primary">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-[13px] font-semibold text-foreground/90">{m.label}</span>
                <span className="flex-1 rule ml-1" />
              </div>
              <div className="space-y-0.5">
                {m.items.map(([key, label, path]) => (
                  <NavLink key={key} to={path} data-testid={`nav-${key.replace(".", "-")}`} className={({ isActive }) => cn("nav-item", isActive && "active")}>{label}</NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 h-16 flex items-center justify-between px-4 lg:px-8 border-b border-border backdrop-blur-md bg-background/80" data-testid="topbar">
          <div className="flex items-center gap-4 min-w-0">
            <div className="hidden md:block">
              <div className="font-serif text-sm font-medium">{today} <span className="text-muted-foreground font-normal" data-testid="bs-date">· {fmtBS(new Date())} BS</span></div>
              <div className="font-mono text-[11px] text-muted-foreground"><Clock /> · {activeOrg?.timezone || "UTC"}</div>
            </div>
            <button onClick={() => setCmd(true)} data-testid="command-palette-trigger" className="flex items-center gap-2 h-9 px-3 rounded-full border border-border bg-card text-sm text-muted-foreground hover:border-primary/50 transition-colors">
              <Search size={14} /> <span className="hidden sm:inline">Jump to…</span> <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-secondary">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-2">
            {user.role === "superadmin" && user.organizations?.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full h-9 gap-2" data-testid="org-switcher-select"><Building2 size={14} /> <span className="max-w-[140px] truncate">{activeOrg?.name}</span> <ChevronDown size={14} /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider">Switch organization</DropdownMenuLabel>
                  {user.organizations.map((o) => <DropdownMenuItem key={o.id} data-testid={`org-option-${o.id}`} onClick={() => switchOrg(o.id)} className={cn(o.id === user.active_org_id && "font-semibold text-primary")}>{o.name}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="theme-toggle-button" onClick={() => setDark((d) => !d)}>{dark ? <Sun size={16} /> : <Moon size={16} />}</Button>
            <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="chat-open-button" onClick={() => window.dispatchEvent(new CustomEvent("avocado:chat-toggle"))}><MessageSquare size={16} />{chatUnread > 0 && <span data-testid="chat-dock-unread" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-mono grid place-items-center">{chatUnread}</span>}</Button>
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="notifications-button" onClick={() => nav("/requests")}><Bell size={16} /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-3 h-10 rounded-full border border-border bg-card hover:border-primary/50 transition-colors" data-testid="user-menu-trigger">
                  <Avatar src={user.avatar} name={user.name} size={30} />
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-medium">{user.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-primary" data-testid="user-role-label">{user.role_label}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/my/attendance")}>My HR</DropdownMenuItem>
                <DropdownMenuItem onClick={() => logout()} data-testid="logout-button" className="text-destructive"><LogOut size={14} className="mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="lg:hidden flex gap-2 overflow-x-auto px-4 py-2 border-b border-border bg-[var(--paper)]" data-testid="mobile-nav">
          {modules.map((m) => <NavLink key={m.id} to={m.items[0][2]} className={cn("nav-item whitespace-nowrap", loc.pathname.startsWith("/" + m.items[0][2].split("/")[1]) && "active")}>{m.label}</NavLink>)}
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto"><Outlet /></main>
        {!loc.pathname.startsWith("/collab/discuss") && <ChatDock />}
      </div>

      <CommandDialog open={cmd} onOpenChange={setCmd}>
        <CommandInput placeholder="Search modules, pages, actions…" data-testid="command-palette-input" />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          <CommandGroup heading="Quick actions">
            <CommandItem onSelect={() => { nav("/my/attendance"); setCmd(false); }}>Punch in / out</CommandItem>
            <CommandItem onSelect={() => { nav("/my/leave"); setCmd(false); }}>Request leave</CommandItem>
            <CommandItem onSelect={() => { nav("/workspace/todo"); setCmd(false); }}>Add a to-do</CommandItem>
          </CommandGroup>
          {modules.map((m) => (
            <CommandGroup key={m.id} heading={m.label}>
              {m.items.map(([key, label, path]) => <CommandItem key={key} onSelect={() => { nav(path); setCmd(false); }} data-testid={`cmd-${key.replace(".", "-")}`}>{label}</CommandItem>)}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
